import AddIcon from '@mui/icons-material/Add'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined'
import PlaylistAddOutlinedIcon from '@mui/icons-material/PlaylistAddOutlined'
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'
import { TableVirtuoso } from 'react-virtuoso'
import { type CSSProperties, type MouseEvent, useEffect, useMemo, useRef, useState } from 'react'
import {
  type SaleInvoice,
  useDeleteSaleInvoiceMutation,
  useGetSaleInvoiceCanHardDeleteQuery,
  useGetSaleInvoicesQuery,
} from '../../store/api/saleInvoiceApi'
import {
  useLazyGetInvoiceCustomsPrintPreviewQuery,
  useLazyGetInvoiceCustomerPrintPreviewQuery,
  useLazyGetInvoicePklPrintPreviewQuery,
} from '../../store/api/saleInvoicePrintApi'
import InvoiceFormDialog from './InvoiceFormDialog'
import InvoiceReceivablesCheckDialog from './InvoiceReceivablesCheckDialog'
import InvoiceAddDoDialog from './InvoiceAddDoDialog'
import InvoicePrintDialog from './InvoicePrintDialog'
import { HIDE_ON_MOBILE_INLINE, HIDE_ON_TABLET_INLINE, TABLE_PAGINATION_COMPACT } from '../../theme/responsive'
import BrandState from '../../components/BrandState'
import ConfirmDialog from '../../components/ConfirmDialog'
import {
  type ColumnFilterAnchorPosition,
  ColumnFilterIcon,
  ColumnFilterPopover,
  type ColumnFilterKind,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  filterBarBtnSx,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  useRowSelection,
} from '../../components/dataList'
import ResizeHandle from '../../components/ResizeHandle'
import { useColumnWidths } from '../../hooks/useColumnWidths'
import { useIsMobile } from '../../hooks/useResponsive'
import { usePagePermission } from '../../hooks/usePagePermission'
import { useSnackbar } from '../../hooks/useSnackbar'
import { getErrorMessage } from '../../utils/error'
import { titleCaseName } from '../../utils/text'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '../../utils/tableSort'
import { usePreferenceColumns } from '@/utils/datasheetColumns/usePreferenceColumns'
import { FuturesColumnsMenu } from '../futures/FuturesColumnsMenu'

type FilterKind = 'search'
type FinalizedFilter = 'all' | 'yes' | 'no'

/** Freeze the invoice No. identifier column when scrolling horizontally (skill /convert-table §7.6). */
const FROZEN_KEYS: ReadonlySet<string> = new Set(['invoiceNo'])
const FROZEN_FALLBACK_WIDTH_DESKTOP: Record<string, number> = { invoiceNo: 110 }
const FROZEN_FALLBACK_WIDTH_MOBILE: Record<string, number> = { invoiceNo: 100 }

/** TablePagination «All» maps to this pageSize (single request). */
const ALL_ROWS_PAGE_SIZE = 1_000_000

const actionToolbarBtnSx: SxProps<Theme> = {
  minWidth: 0,
  px: 0.75,
  py: 0.2,
  minHeight: 26,
  fontSize: '0.72rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

type ColumnKind = 'text' | 'date' | 'datetime' | 'number'
type ColumnDef = {
  key: keyof SaleInvoice
  label: string
  kind?: ColumnKind
  align?: 'right'
  filter?: ColumnFilterKind
  wrap?: boolean
  width?: number
}

/**
 * Column order + captions follow Access `frmCtrlSalesQryInvoice` datasheet
 * `ColumnOrder` (source: `form_web_ui_structure_sorted.csv`, verified via
 * `/convert-structure --datasheet`). Date format `d-mmm-yyyy`, numbers `Standard`/2dp
 * right-aligned. customerInvPrice / docsPresentationDate are text in the view.
 */
const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'id', label: 'Invoice ID', kind: 'number', align: 'right' },
  { key: 'contractRef', label: 'Contract Ref' },
  { key: 'buyerName', label: 'Buyer Name' },
  { key: 'buyersRef', label: 'Buyer Ref' },
  { key: 'contractDate', label: 'Contract Date', kind: 'date' },
  { key: 'contractWeightKg', label: 'Contract Weight Kgs', kind: 'number', align: 'right' },
  { key: 'quality', label: 'Quality', filter: 'checkboxes' },
  { key: 'deliveryTerms', label: 'Terms', filter: 'checkboxes' },
  { key: 'warehouse', label: 'Warehouse' },
  { key: 'paymentTerms', label: 'Payment Terms' },
  { key: 'paymentWeightTerms', label: 'Payment Weight Terms' },
  { key: 'contractType', label: 'Contact Type' },
  { key: 'toBank', label: 'To Bank' },
  { key: 'invoiceNo', label: 'No. INV' },
  { key: 'vatNo', label: 'No. VAT' },
  { key: 'lstDoNo', label: 'For DO', wrap: true, width: 160 },
  { key: 'customsInvNo', label: 'Customs INV No' },
  { key: 'customerInvoiceNumber', label: 'Customer Invoice Number' },
  { key: 'invoiceType', label: 'Invoice Type', filter: 'checkboxes' },
  { key: 'invoiceDate', label: 'Custom Invoice Date', kind: 'date' },
  { key: 'commercialInvDateShow', label: 'Commercial Invoice Date', kind: 'date' },
  { key: 'noBag', label: 'No Bag', kind: 'number', align: 'right' },
  { key: 'packaging', label: 'Package' },
  { key: 'goodsDescription', label: 'Goods Description', wrap: true, width: 220 },
  { key: 'othersDescription', label: 'Others Description', wrap: true, width: 220 },
  { key: 'finalPriceUsdmt', label: 'Final Price USD/Mt', kind: 'number', align: 'right' },
  { key: 'fpFx', label: 'FX', kind: 'number', align: 'right' },
  { key: 'finalPriceVndkg', label: 'Final Price VND/Kg', kind: 'number', align: 'right' },
  { key: 'ppClosingPrice', label: 'Closing Price', kind: 'number', align: 'right' },
  { key: 'ppDiffs', label: 'Diffs', kind: 'number', align: 'right' },
  { key: 'ppPriceUsdmt', label: 'Provi Price', kind: 'number', align: 'right' },
  { key: 'ppFx', label: 'FX2', kind: 'number', align: 'right' },
  { key: 'ppPriceVndkg', label: 'Price VND', kind: 'number', align: 'right' },
  { key: 'stoploss', label: 'Stoploss', kind: 'number', align: 'right' },
  { key: 'customInvGw', label: 'Customs GW', kind: 'number', align: 'right' },
  { key: 'customInvNw', label: 'Customs NW', kind: 'number', align: 'right' },
  { key: 'customInvPrice', label: 'Custom INV Price', kind: 'number', align: 'right' },
  { key: 'customInvTotal', label: 'Custom INV Total', kind: 'number', align: 'right' },
  { key: 'customerInvGw', label: 'Customer GW', kind: 'number', align: 'right' },
  { key: 'customerInvNw', label: 'Customer NW', kind: 'number', align: 'right' },
  { key: 'customerInvPrice', label: 'Customer INV Price' },
  { key: 'customerInvTotal', label: 'Customer INV Total', kind: 'number', align: 'right' },
  { key: 'customerInvAdjust', label: 'Customer INV Adjust', kind: 'number', align: 'right' },
  { key: 'customerInvAtd', label: 'Customer INV ATD', kind: 'date' },
  { key: 'receiptWh', label: 'WH Receipt' },
  { key: 'receiptGw', label: 'Gross Landed Weight', kind: 'number', align: 'right' },
  { key: 'receiptNw', label: 'Net Landed Weight', kind: 'number', align: 'right' },
  { key: 'released', label: 'Released', kind: 'number', align: 'right' },
  { key: 'toBeReleased', label: 'To be released', kind: 'number', align: 'right' },
  { key: 'toBePaid', label: 'To be paid', kind: 'number', align: 'right' },
  { key: 'blNumber', label: 'B/L No' },
  { key: 'rcEtaAta', label: 'ETA/ATA', kind: 'date' },
  { key: 'docsPresentationDate', label: 'Docs sent date' },
  { key: 'rcTrackingNo', label: 'Tracking no' },
  { key: 'rcDocsDeliveredDate', label: 'Docs delivered date', kind: 'date' },
  { key: 'rcDueDate', label: 'Due date', kind: 'date' },
  { key: 'rcPaidDate', label: 'Paid date', kind: 'date' },
  { key: 'rcPaidAmount', label: 'Paid amount', kind: 'number', align: 'right' },
  { key: 'rcPaidDetails', label: 'Paid details', wrap: true, width: 200 },
  { key: 'blDate', label: 'B/L Date', kind: 'date' },
]

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const formatAccessDate = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  return `${day}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()}`
}

const formatAccessDateTime = (value: unknown): string => {
  if (!value) return ''
  const d = new Date(String(value))
  if (Number.isNaN(d.getTime())) return String(value)
  const day = `${d.getDate()}`.padStart(2, '0')
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  return `${day}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()} ${hh}:${mm}`
}

const formatAccessNumber = (value: number): string => {
  if (Number.isInteger(value)) return value.toLocaleString('en-US')
  return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const sumNumericColumn = (rows: SaleInvoice[], key: keyof SaleInvoice): number | null => {
  let total = 0
  let count = 0
  for (const row of rows) {
    const value = row[key]
    if (typeof value !== 'number' || !Number.isFinite(value)) continue
    total += value
    count += 1
  }
  return count > 0 ? total : null
}

interface SaleControlInvoiceViewProps {
  deliveryPeriodStart?: string
  deliveryPeriodEnd?: string
  finalizedFilter?: FinalizedFilter
}

export default function SaleControlInvoiceView({
  deliveryPeriodStart = '',
  deliveryPeriodEnd = '',
  finalizedFilter = 'all',
}: SaleControlInvoiceViewProps) {
  const { canEdit } = usePagePermission()
  const isMobile = useIsMobile()
  const FROZEN_FALLBACK_WIDTH = isMobile ? FROZEN_FALLBACK_WIDTH_MOBILE : FROZEN_FALLBACK_WIDTH_DESKTOP
  const snack = useSnackbar()
  const { selectedId, setSelectedId, toggleSelect } = useRowSelection<number>()
  const [confirmDelete, setConfirmDelete] = useState<SaleInvoice | null>(null)
  // Dialog states (frmCtrlSalesQryInvoiceAdd + _ReceivablesCheck + _DOEdit).
  const [formState, setFormState] = useState<{ mode: 'new' | 'edit' | 'copy'; invoiceId: number | null } | null>(null)
  const [receivablesId, setReceivablesId] = useState<number | null>(null)
  const [addDoState, setAddDoState] = useState<{ invoiceId: number; row: { customsInvNo: string | null; invoiceDate: string | null } } | null>(null)
  // PKL chooser (frmCtrlSalesQryInvoicePKL_Choose ComboBox) → print mode.
  const [pklChooser, setPklChooser] = useState<{ customerInvoiceNumber: string } | null>(null)
  // Print preview (Customs / Customer / PKL share one InvoicePrintDialog).
  const [printState, setPrintState] = useState<{ open: boolean; title: string; html: string; loading: boolean }>({
    open: false,
    title: '',
    html: '',
    loading: false,
  })
  const [triggerCustomsPreview] = useLazyGetInvoiceCustomsPrintPreviewQuery()
  const [triggerCustomerPreview] = useLazyGetInvoiceCustomerPrintPreviewQuery()
  const [triggerPklPreview] = useLazyGetInvoicePklPrintPreviewQuery()

  const { data: deleteAccess } = useGetSaleInvoiceCanHardDeleteQuery(undefined, {
    refetchOnMountOrArgChange: true,
  })
  const canHardDelete = canEdit && (deleteAccess?.canDelete ?? false)
  const [deleteSaleInvoice, { isLoading: deleting }] = useDeleteSaleInvoiceMutation()

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(-1)
  const { getWidth, startResize } = useColumnWidths('sale-invoice.list')

  const [searchTerm, setSearchTerm] = useState('')
  const [sortState, setSortState] = useState<TableSortState<keyof SaleInvoice> | null>(null)

  const filterPopover = useFilterPopover<FilterKind>()
  const closeFilterPopover = () => filterPopover.close()
  const openFilterPopover = (kind: FilterKind, e: MouseEvent<HTMLElement>) => filterPopover.open(kind, e)

  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: keyof SaleInvoice
    position: ColumnFilterAnchorPosition
  } | null>(null)
  const closeColPopover = () => setColPopover(null)

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPage(0)
    closeFilterPopover()
  }

  const columnsCtrl = usePreferenceColumns({
    scope: 'sale_control',
    prefKey: 'columns',
    subKey: 'invoice',
    allColumns: TABLE_COLUMNS,
    widthsTableKey: 'sale-invoice.list',
  })
  const { visibleColumns, openMenu: openColMenu } = columnsCtrl

  const {
    data: list,
    isLoading,
    isFetching,
    error,
  } = useGetSaleInvoicesQuery(
    {
      page: 1,
      pageSize: ALL_ROWS_PAGE_SIZE,
      ...(deliveryPeriodStart ? { deliveryPeriodFrom: deliveryPeriodStart } : {}),
      ...(deliveryPeriodEnd ? { deliveryPeriodTo: deliveryPeriodEnd } : {}),
      finalizedFilter,
    },
    { refetchOnMountOrArgChange: true },
  )

  const baseRows = useMemo(() => list?.items ?? [], [list])
  const filteredRows = useMemo(
    () =>
      baseRows.filter(
        (row) =>
          rowMatchesSearch(row, searchTerm, TABLE_COLUMNS) &&
          rowMatchesAllColumnFilters(row, columnFilters.filters),
      ),
    [baseRows, columnFilters.filters, searchTerm],
  )
  const sortedRows = useMemo(() => sortTableRows(filteredRows, sortState, TABLE_COLUMNS), [filteredRows, sortState])
  const rows = useMemo(() => {
    if (rowsPerPage === -1) return sortedRows
    const start = page * rowsPerPage
    return sortedRows.slice(start, start + rowsPerPage)
  }, [page, rowsPerPage, sortedRows])
  const totalRowValues = useMemo(
    () =>
      new Map(
        visibleColumns
          .filter((col) => col.kind === 'number' && col.key !== 'id')
          .map((col) => [col.key, sumNumericColumn(filteredRows, col.key)] as const),
      ),
    [filteredRows, visibleColumns],
  )
  const popoverColumn = useMemo<ColumnDef | null>(() => {
    if (!colPopover) return null
    return TABLE_COLUMNS.find((c) => c.key === colPopover.key) ?? null
  }, [colPopover])
  const popoverKind = useMemo<ColumnFilterKind>(
    () => (popoverColumn ? inferColumnFilterKind(popoverColumn as never, baseRows) : 'text'),
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () => (popoverKind === 'checkboxes' && colPopover ? distinctColumnValues(baseRows, String(colPopover.key)) : undefined),
    [popoverKind, colPopover, baseRows],
  )

  const idRangeText = useMemo(() => {
    if (rows.length === 0) return 'ID: —'
    let min = Number.POSITIVE_INFINITY
    let max = Number.NEGATIVE_INFINITY
    for (const r of rows) {
      const id = Number(r.id)
      if (!Number.isFinite(id)) continue
      if (id < min) min = id
      if (id > max) max = id
    }
    if (!Number.isFinite(min) || !Number.isFinite(max)) return 'ID: —'
    return `ID: ${min}–${max}`
  }, [rows])
  const minTableWidth = Math.max(960, visibleColumns.length * 96)
  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}:${visibleColumns.length}`

  const toggleSort = (key: keyof SaleInvoice) => {
    setSortState((current) => toggleTableSort(current, key))
    setPage(0)
  }

  const selectedRow = useMemo<SaleInvoice | null>(
    () => rows.find((r) => r.id === selectedId) ?? null,
    [rows, selectedId],
  )

  // Row-check helper — Access btnEdit/btnCopy/btnReceivables/btnAddDO/btnDelete/
  // btnPrint* all begin with `check_text = Form![subForm]!invoice_scs_id` and
  // MsgBox "Please choose a record!" when empty.
  const requireRow = (): SaleInvoice | null => {
    if (!selectedRow) {
      snack.warning('Please choose a record!')
      return null
    }
    return selectedRow
  }

  // Access frmCtrlSalesQryInvoice has no Form_Load GoToRecord → Access defaults to the
  // FIRST record. Auto-select the first row once after the list finishes loading; do
  // not override a manual selection or re-run on refetch.
  const autoSelectedRef = useRef(false)
  useEffect(() => {
    if (autoSelectedRef.current) return
    if (isFetching) return
    if (selectedId != null) return
    if (sortedRows.length === 0) return
    autoSelectedRef.current = true
    setSelectedId(sortedRows[0].id)
  }, [isFetching, sortedRows, selectedId, setSelectedId])

  // ── Toolbar actions (Access frmCtrlSalesMainSubFormInvoice, Left order) ──────
  const openForm = (mode: 'new' | 'edit' | 'copy') => {
    if (!canEdit && mode !== 'copy') return // Access: btnNew/btnEdit gated; btnCopy not gated
    if ((mode === 'edit' || mode === 'copy') && !requireRow()) return
    setFormState({ mode, invoiceId: mode === 'new' ? null : selectedRow?.id ?? null })
  }
  const handleReceivablesCheck = () => {
    const row = requireRow() // btnReceivables — not permission-gated in Access
    if (!row) return
    setReceivablesId(row.id)
  }
  const handleAddDo = () => {
    const row = requireRow() // btnAddDO — permission gating commented out in Access
    if (!row) return
    setAddDoState({ invoiceId: row.id, row: { customsInvNo: row.customsInvNo, invoiceDate: row.invoiceDate } })
  }
  const runPrintPreview = async (
    title: string,
    trigger: () => Promise<{ title: string; html: string; message?: string }>,
  ) => {
    setPrintState({ open: true, title, html: '', loading: true })
    try {
      const res = await trigger()
      setPrintState({ open: true, title: res.title || title, html: res.html, loading: false })
    } catch (err) {
      const e = err as { status?: number; data?: { detail?: string } }
      snack.warning(e?.status === 422 ? e.data?.detail || 'Cannot print this record' : 'Failed to generate print preview.')
      setPrintState({ open: false, title: '', html: '', loading: false })
    }
  }
  const handlePrintCustoms = () => {
    if (!canEdit) return
    const row = requireRow()
    if (!row) return
    if (!row.customsInvNo) {
      snack.warning('Customs invoice no must be not null!')
      return
    }
    void runPrintPreview(`Customs Invoice (${row.customsInvNo})`, () =>
      triggerCustomsPreview(row.customsInvNo!).unwrap(),
    )
  }
  const handlePrintCustomer = () => {
    if (!canEdit) return
    const row = requireRow()
    if (!row) return
    if (!row.customerInvoiceNumber) {
      snack.warning('Customer invoice no must be not null!')
      return
    }
    void runPrintPreview(`Customer Invoice (${row.customerInvoiceNumber})`, () =>
      triggerCustomerPreview(row.customerInvoiceNumber!).unwrap(),
    )
  }
  const handlePrintPkl = () => {
    if (!canEdit) return
    const row = requireRow()
    if (!row) return
    if (!row.customerInvoiceNumber) {
      snack.warning('Customer invoice number must not be null!')
      return
    }
    // Access opens frmCtrlSalesQryInvoicePKL_Choose (ComboBox mode) before printing.
    setPklChooser({ customerInvoiceNumber: row.customerInvoiceNumber })
  }
  const runPklPrint = (mode: 'OnSpot' | 'OnSpotDone' | 'Direct') => {
    const cin = pklChooser?.customerInvoiceNumber
    setPklChooser(null)
    if (!cin) return
    void runPrintPreview(`Packing List (${cin})`, () =>
      triggerPklPreview({ customerInvoiceNumber: cin, mode }).unwrap(),
    )
  }

  const handleDeleteClick = () => {
    if (!requireRow()) return
    setConfirmDelete(selectedRow)
  }

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return
    try {
      await deleteSaleInvoice(confirmDelete.id).unwrap()
      snack.success('Successfully Deleted')
      if (selectedId === confirmDelete.id) setSelectedId(null)
      setConfirmDelete(null)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Delete invoice failed'))
    }
  }

  const cellValue = (row: SaleInvoice, col: ColumnDef): string => {
    const val = row[col.key]
    if (val == null) return ''
    if (col.kind === 'date') return formatAccessDate(val)
    if (col.kind === 'datetime') return formatAccessDateTime(val)
    if (col.kind === 'number' && typeof val === 'number') return formatAccessNumber(val)
    if (typeof val === 'number') return formatAccessNumber(val)
    if (col.key === 'buyerName') return titleCaseName(val)
    return String(val)
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        '& > .app-dataframe': { flex: 1, minHeight: 0, height: 'auto' },
      }}
    >
      <FilterBar
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAllFilters}
        activeFilterCount={activeFilterCount}
        rightSlot={
          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
            {/* Button order follows Access `frmCtrlSalesMainSubFormInvoice` (Left order):
                New · Edit · Copy · Receivables Check · Add DO · Delete · Print-Customs ·
                Print-Customer · Print-PKL. (Command90 "Print - Dispatch" has no VBA
                handler in Access — omitted.) */}
            <Button
              variant="contained"
              size="small"
              color="success"
              sx={actionToolbarBtnSx}
              disabled={!canEdit}
              onClick={() => openForm('new')}
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              title="New"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>New</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="warning"
              sx={actionToolbarBtnSx}
              disabled={!selectedRow || !canEdit}
              onClick={() => openForm('edit')}
              startIcon={<EditOutlinedIcon sx={{ fontSize: 16 }} />}
              title="Edit"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Edit</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="primary"
              sx={actionToolbarBtnSx}
              disabled={!selectedRow}
              onClick={() => openForm('copy')}
              startIcon={<ContentCopyIcon sx={{ fontSize: 16 }} />}
              title="Copy"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Copy</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="secondary"
              sx={actionToolbarBtnSx}
              disabled={!selectedRow}
              onClick={handleReceivablesCheck}
              startIcon={<FactCheckOutlinedIcon sx={{ fontSize: 16 }} />}
              title="Receivables Check"
            >
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Receivables Check</Box>
            </Button>
            <Button
              variant="outlined"
              size="small"
              color="secondary"
              sx={actionToolbarBtnSx}
              disabled={!selectedRow}
              onClick={handleAddDo}
              startIcon={<PlaylistAddOutlinedIcon sx={{ fontSize: 16 }} />}
              title="Add DO"
            >
              <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Add DO</Box>
            </Button>
            {canHardDelete && (
              <Button
                variant="outlined"
                size="small"
                color="error"
                sx={actionToolbarBtnSx}
                disabled={!selectedRow || !canEdit}
                onClick={handleDeleteClick}
                startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
                title="Delete"
              >
                <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>Delete</Box>
              </Button>
            )}
            <Button
              variant="outlined"
              size="small"
              sx={{ ...actionToolbarBtnSx, color: 'primary.main', borderColor: 'primary.main' }}
              disabled={!selectedRow || !canEdit}
              onClick={handlePrintCustoms}
              startIcon={<PrintOutlinedIcon sx={{ fontSize: 16 }} />}
              title="Print - Customs"
            >
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Print-</Box>Customs
            </Button>
            <Button
              variant="outlined"
              size="small"
              sx={{ ...actionToolbarBtnSx, color: 'primary.main', borderColor: 'primary.main' }}
              disabled={!selectedRow || !canEdit}
              onClick={handlePrintCustomer}
              startIcon={<PrintOutlinedIcon sx={{ fontSize: 16 }} />}
              title="Print - Customer"
            >
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Print-</Box>Customer
            </Button>
            <Button
              variant="outlined"
              size="small"
              sx={{ ...actionToolbarBtnSx, color: 'primary.main', borderColor: 'primary.main' }}
              disabled={!selectedRow || !canEdit}
              onClick={handlePrintPkl}
              startIcon={<PrintOutlinedIcon sx={{ fontSize: 16 }} />}
              title="Print - PKL"
            >
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Print-</Box>PKL
            </Button>
          </Stack>
        }
      >
        <FilterButton label="Search" active={!!searchTerm.trim()} onClick={(e) => openFilterPopover('search', e)} />
        <Button
          size="small"
          variant="outlined"
          endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
          onClick={(e) => openColMenu(e.currentTarget)}
          sx={filterBarBtnSx}
          title="Columns"
        >
          <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
          <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
        </Button>
      </FilterBar>

      <Divider sx={{ mb: 0.5 }} />

      {error ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          Failed to load Invoices.
        </Alert>
      ) : null}

      <Box className="app-dataframe">
        <Box
          className="app-dataframe-toolbar"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 0.5,
            flexWrap: 'wrap',
            minHeight: 24,
            py: 0,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Total: {filteredRows.length}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Loaded: {baseRows.length}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              {idRangeText}
            </Typography>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={TABLE_PAGINATION_COMPACT}
            />
          </Stack>
        </Box>

        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={closeFilterPopover}
          maxWidth={320}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); setPage(0) }}
              placeholder="Invoice no, contract, buyer..."
              label="Search invoices"
              onClear={closeFilterPopover}
            />
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={closeColPopover}
          label={popoverColumn?.label ?? ''}
          kind={(popoverKind === 'none' ? 'text' : popoverKind)}
          value={colPopover ? columnFilters.filters[String(colPopover.key)] : undefined}
          onChange={(v) => {
            if (!colPopover) return
            columnFilters.setFilter(String(colPopover.key), v)
            setPage(0)
          }}
          options={popoverOptions}
        />

        <Box sx={{ flex: 1, minHeight: 320, overflow: 'hidden' }}>
          {error ? (
            <BrandState
              tone="error"
              title="Could not load Invoices"
              description="Please refresh the page or contact the system administrator if the issue continues."
            />
          ) : isLoading || isFetching ? (
            <BrandState tone="loading" title="Loading…" description="Please wait." />
          ) : rows.length === 0 ? (
            <BrandState
              title="No Invoice found"
              description="Try adjusting the saved delivery period, finalized filter, or the table filters above."
            />
          ) : (
            <TableVirtuoso
              key={gridKey}
              style={{ height: '100%' }}
              data={rows}
              fixedHeaderContent={() => (
                <TableRow>
                  {visibleColumns.map((col) => {
                    const key = String(col.key)
                    const isFrozen = FROZEN_KEYS.has(key)
                    const w = getWidth(key) ?? col.width ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                    const headerStyle: CSSProperties = {}
                    if (w) { headerStyle.width = w; headerStyle.minWidth = w; headerStyle.maxWidth = w }
                    if (isFrozen) {
                      headerStyle.position = 'sticky'
                      headerStyle.top = 0
                      headerStyle.left = 0
                      headerStyle.zIndex = 100
                    }
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className="app-table-header-cell"
                        onClick={() => toggleSort(col.key)}
                        style={Object.keys(headerStyle).length ? headerStyle : undefined}
                        sx={{
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                          userSelect: 'text',
                          ...(isFrozen ? {} : { position: 'relative' }),
                        }}
                      >
                        {col.label}
                        {getTableSortSuffix(sortState, col.key)}
                        <ColumnFilterIcon
                          active={columnFilters.isActive(key)}
                          onClick={(position) => setColPopover({ key: col.key, position })}
                          ariaLabel={`Filter ${col.label}`}
                        />
                        <ResizeHandle onMouseDown={(e) => startResize(key, e)} />
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              fixedFooterContent={() => (
                <TableRow>
                  {visibleColumns.map((col, index) => {
                    const key = String(col.key)
                    const isFrozen = FROZEN_KEYS.has(key)
                    const total = totalRowValues.get(col.key)
                    const val = total == null ? '' : formatAccessNumber(total)
                    const w = getWidth(key) ?? col.width ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className={isFrozen ? 'app-sticky-col' : undefined}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          py: 0.5,
                          bgcolor: 'background.paper',
                          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {index === 0 ? 'Total' : val}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              itemContent={(_: number, row: SaleInvoice) =>
                visibleColumns.map((col) => {
                  const key = String(col.key)
                  const isFrozen = FROZEN_KEYS.has(key)
                  const val = cellValue(row, col)
                  const w = getWidth(key) ?? col.width ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[key] : undefined)
                  const wrap = col.wrap || getWidth(key) != null
                  if (wrap) {
                    return (
                      <TableCell
                        key={col.key}
                        align={col.align}
                        className={isFrozen ? 'app-sticky-col' : undefined}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5, verticalAlign: 'top' }}
                      >
                        <Box component="span" className="app-table-cell-wrap">
                          {val || '-'}
                        </Box>
                      </TableCell>
                    )
                  }
                  return (
                    <TableCell
                      key={col.key}
                      align={col.align}
                      className={isFrozen ? 'app-sticky-col' : undefined}
                      style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                      sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'top' }}
                    >
                      <Box component="span" className="app-table-cell-text" title={val || '-'} sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {val || '-'}
                      </Box>
                    </TableCell>
                  )
                })
              }
              components={{
                Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />,
                TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
                TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
                TableRow: (props: any) => {
                  const idx = Number((props as any)['data-item-index'])
                  const rowId = Number.isFinite(idx) ? rows[idx]?.id : null
                  return (
                    <TableRow
                      {...props}
                      hover
                      selected={rowId != null && rowId === selectedId}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        if (rowId == null) return
                        toggleSelect(rowId)
                      }}
                    />
                  )
                },
              }}
            />
          )}
        </Box>
      </Box>

      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete Invoice"
        message={confirmDelete ? `Do you want to delete Invoice = '${confirmDelete.invoiceNo ?? ''}'?` : ''}
        destructive
        loading={deleting}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setConfirmDelete(null)}
      />
      <FuturesColumnsMenu controller={columnsCtrl} />

      {formState && (
        <InvoiceFormDialog
          open
          mode={formState.mode}
          invoiceId={formState.invoiceId}
          canEdit={canEdit}
          onClose={() => setFormState(null)}
          onSaved={() => setSelectedId(null)}
        />
      )}
      <InvoiceReceivablesCheckDialog
        open={receivablesId != null}
        invoiceId={receivablesId}
        canEdit={canEdit}
        onClose={() => setReceivablesId(null)}
        onSaved={() => { /* row rc_* updated; list refetch via tag invalidation */ }}
      />
      <InvoiceAddDoDialog
        open={addDoState != null}
        invoiceId={addDoState?.invoiceId ?? null}
        row={addDoState?.row ?? null}
        canEdit={canEdit}
        onClose={() => setAddDoState(null)}
        onSaved={() => { /* invoice_scs_details updated; list refetch via tag invalidation */ }}
      />

      {/* frmCtrlSalesQryInvoicePKL_Choose — ComboBox mode selector before PKL print. */}
      <Dialog open={pklChooser != null} onClose={() => setPklChooser(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ py: 1 }}>
          <Typography component="span" variant="subtitle1" sx={{ fontWeight: 600 }}>Print - PKL</Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Select the packing list type to print.
          </Typography>
          <Stack spacing={1}>
            <Button variant="outlined" onClick={() => runPklPrint('OnSpot')}>On Spot</Button>
            <Button variant="outlined" onClick={() => runPklPrint('OnSpotDone')}>On Spot (Done)</Button>
            <Button variant="outlined" onClick={() => runPklPrint('Direct')}>Direct</Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPklChooser(null)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      <InvoicePrintDialog
        open={printState.open}
        title={printState.title}
        html={printState.html}
        loading={printState.loading}
        canEdit={canEdit}
        onClose={() => setPrintState({ open: false, title: '', html: '', loading: false })}
      />
    </Box>
  )
}
